import type { LinkElementValue } from '@vedalib/editor/lib/elements'
import type { FileUsage } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'
import { FileTypeEnum } from 'utils'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import {
  FieldColorPicker,
  FieldFile,
  FieldInput,
  FieldSegmented,
  FieldTextArea,
  FormField,
} from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import Button from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { useBrandColorOptions } from 'services/Branding/fieldConfigs/useFieldConfigs'
import { updateBlock } from 'services/Store/Project/actions'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlockId } from 'services/Store/Project/selectors'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { chainName } from 'utils/form'

import { getFileInfo } from './helper'
import { labelFromUrl } from './helper'
import type { LinkFormValueType } from './linkTypes'

const useLinkPlaceholder = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<LinkElementValue>(name, 1)
  const placeholder = labelFromUrl(value?.linkURL) || t('elements.link.placeholder.linkText')
  return useMemo(() => ({ props: { placeholder } }), [placeholder])
}

const useShowPreview = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<LinkElementValue>(name, 1)
  return useMemo(() => ({ hidden: !value?.showPreview }), [value?.showPreview])
}

const useShowCustomImage = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<LinkElementValue>(name, 1)
  return useMemo(() => ({ hidden: !value?.showCustomImage }), [value?.showCustomImage])
}

const LINK_URL = genElementValueField({
  as: FieldInput,
  name: 'linkURL',
  label: t('input.label.linkAddress'),
  layout: 'vertical',
  rules: [{ max: 2048, message: t('input.error.invalidUrl') }],
  props: { placeholder: t('input.placeholder.enterUrl') },
})

const LINK_LABEL = genElementValueField({
  as: FieldInput,
  name: 'linkLabel',
  label: t('elements.link.form.linkName'),
  layout: 'vertical',
  rules: [{ max: 256 }],
  useConfigs: [useLinkPlaceholder],
})

const SHOW_PREVIEW = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'showPreview',
  label: t('elements.link.form.preview'),
})

const TITLE = genElementValueField({
  as: FieldInput,
  name: 'title',
  label: t('input.label.title'),
  layout: 'vertical',
  rules: [{ max: 256 }],
  props: { placeholder: t('elements.link.placeholder.title') },
  useConfigs: [useShowPreview],
})

const DESCRIPTION = genElementValueField({
  as: FieldTextArea,
  name: 'description',
  label: t('input.label.description'),
  layout: 'vertical',
  rules: [{ max: 1080 }],
  props: { placeholder: t('elements.link.placeholder.description') },
  useConfigs: [useShowPreview],
})

const SHOW_CUSTOM_IMAGE = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'showCustomImage',
  label: t('elements.link.form.upload'),
  useConfigs: [useShowPreview],
})

const IMAGE = genElementValueField({
  as: FieldFile<FileUsage>,
  name: 'image',
  props: {
    fileType: FileTypeEnum.IMAGE,
    nullable: true,
    preview: true,
    showSource: true,
    hasObjectFit: true,
    hasAccessibility: true,
  },
  useConfigs: [useShowPreview, useShowCustomImage],
})

const OVERLAY = genElementValueField({
  name: 'overlay',
  as: FieldColorPicker<string | null>,
  label: t('input.label.overlay'),
  defaultValue: null,
  props: { labeled: true, resettable: true },
  useConfigs: [useBrandColorOptions, useShowPreview],
})

const TARGET = genElementValueField({
  as: FieldSegmented<'_blank' | '_self'>,
  name: 'target',
  label: t('elements.link.form.target'),
  defaultValue: '_blank',
  props: {
    options: [
      { value: '_blank', label: t('input.option.no') },
      { value: '_self', label: t('input.option.yes') },
    ],
  },
})

const LinkFormValue: LinkFormValueType = ({ name, element }) => {
  const dispatch = useProjectDispatch()
  const blockId = useProjectContext(getBlockId)

  const onChange = useCallback(
    async (value: unknown) => blockId && dispatch(updateBlock({ id: blockId, name, value })),
    [blockId, dispatch, name],
  )

  const onClick = useCallback(async () => {
    const data = await getFileInfo(element.value, name)
    onChange(Object.assign({}, element.value, data.value))
  }, [element.value, name, onChange])

  const values = element.value

  return (
    <Padding bottom gap sides>
      <FormField config={chainName(name, LINK_URL)} />
      <FormField config={chainName(name, LINK_LABEL)} />
      <FormField config={chainName(name, TARGET)} />
      <FormField config={chainName(name, SHOW_PREVIEW)} />
      <FormField config={chainName(name, TITLE)} />
      <FormField config={chainName(name, DESCRIPTION)} />
      {values.showPreview && (
        <Typography styleType='hint'>{t('elements.link.form.uploadImageHint')}</Typography>
      )}
      <FormField config={chainName(name, SHOW_CUSTOM_IMAGE)} />
      <FormField config={chainName(name, IMAGE)} />
      <FormField config={chainName(name, OVERLAY)} />
      {values.showPreview && (
        <Button
          disabled={!values.linkURL && !values.title && !values.description}
          name='request'
          onClick={onClick}
          styleType='secondary'
          fluid
        >
          {t('elements.link.form.fillAuto')}
        </Button>
      )}
    </Padding>
  )
}

export default LinkFormValue
